import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'

export const PageHeader: React.FC<{
  title: React.ReactNode
  subTitle?: string
  variant?: 'primary' | 'secondary'
  onClick?: () => void
  rightAlignedComponent?: React.ReactNode
}> = ({ title, subTitle, variant = 'secondary', onClick, rightAlignedComponent }) => {
  return (
    <HeaderStyled data-testid='PageHeader' data-variant={variant} data-has-on-click={!!onClick}>
      <div onClick={onClick}>
        <h1>
          {title}
          {onClick && <Icon name='SolidChevronDown' color='currentColor' size='2.4rem' />}
        </h1>
        {subTitle && <p>{subTitle}</p>}
      </div>

      {rightAlignedComponent ? rightAlignedComponent : null}
    </HeaderStyled>
  )
}

// ======
// Styles
// ======

const HeaderStyled = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacing.smx2,
  boxSizing: 'border-box',
  minHeight: '5.2rem',
  padding: `${spacing.smx1} ${spacing.md}`,

  background: colors.lavender[750],
  color: colors.white,

  '> div': {
    fontFamily: font.family.primary,

    '> *': { margin: 0, padding: 0 },

    '> h1': {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx2,
      textTransform: 'unset',
      textAlign: 'left',
      background: 'unset',
      color: colors.white,
    },

    '> p': {
      color: colors.lavender[100],
      textAlign: 'left',
      fontWeight: font.weight.medium,
      fontSize: font.size.lg.fontSize,
      lineHeight: font.size.lg.lineHeight,
      letterSpacing: font.size.lg.letterSpacing,
    },
  },

  '> a, > button': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  '> a': {
    textDecoration: 'none',
    color: 'unset',
    userSelect: 'none',
    transition: 'opacity 0.25s ease-out',
    WebkitTapHighlightColor: 'transparent',

    '@media (hover: hover) and (pointer: fine)': { ':hover': { opacity: 0.75 } },
    ':active': { opacity: 0.5 },
  },

  '&[data-variant="primary"]': {
    h1: {
      fontWeight: font.weight.bold,
      fontSize: font.size.xl4.fontSize,
      lineHeight: font.size.xl4.lineHeight,
      letterSpacing: font.size.xl4.letterSpacing,
    },
  },

  '&[data-variant="secondary"]': {
    h1: {
      fontWeight: font.weight.semibold,
      fontSize: font.size.xl2.fontSize,
      lineHeight: font.size.xl2.lineHeight,
      letterSpacing: font.size.xl2.letterSpacing,
    },
  },

  '&[data-has-on-click="true"] > div:first-of-type': {
    position: 'relative',

    '::after': {
      content: '""',
      position: 'absolute',
    },
    background: colors.lavender[750],
    color: colors.white,
    border: 'none',
    borderRadius: radius.lgx1,
    fontFamily: font.family.primary,
    fontWeight: font.weight.semibold,
    fontSize: font.size.xl2.fontSize,
    letterSpacing: font.size.xl2.letterSpacing,
    lineHeight: font.size.xl2.lineHeight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s ease',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': { background: colors.lavender[800] },
    },
    '&:active': { transform: 'scale(0.95)', opacity: 0.75 },
    '&:focus-visible': { outline: `0.2rem solid ${colors.white}`, outlineOffset: '0.2rem' },
  },
})
